import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Button } from '../../../components/ui/Button';
import { Card, CardContent } from '../../../components/ui/Card';
import { FormField, Input } from '../../../components/ui/FormField';
import { FormSelect } from '../../../components/ui/FormSelect';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { Money } from '../../../components/ui/Money';
import { Skeleton } from '../../../components/ui/Skeleton';
import { useToast } from '../../../components/ui/Toast';
import { Edit2, Save, X, Calendar, User, TrendingUp, Info } from 'lucide-react';
import { usePageTitle } from '../../../lib/hooks';
import { PageHeader } from '../../../components/layout/PageHeader';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../lib/locale';
import { usePidiSchemeDetail, useUpdatePidiScheme } from '../../../features/pidi/network';
import { agentHooks } from '../../../features/agents/hooks';
import Decimal from 'decimal.js';

export const Route = createFileRoute('/_auth/pidi-schemes/$id')({
  component: PidiSchemeDetailPage,
});

function PidiSchemeDetailPage() {
  const { id } = Route.useParams();
  usePageTitle('PIDI Scheme Details');
  const { toast } = useToast();

  const { data: scheme, isLoading, error } = usePidiSchemeDetail(id);
  const { data: agentsRes } = agentHooks.useList({ limit: 1000 });
  const agents = agentsRes?.data || [];
  const updateMutation = useUpdatePidiScheme();

  const [isEditing, setIsEditing] = React.useState(false);

  // Form fields
  const [displayName, setDisplayName] = React.useState('');
  const [dailyAmount, setDailyAmount] = React.useState('');
  const [durationDays, setDurationDays] = React.useState('');
  const [agentId, setAgentId] = React.useState('');
  const [status, setStatus] = React.useState<string>('ACTIVE');
  const [startDate, setStartDate] = React.useState('');

  React.useEffect(() => {
    if (scheme) {
      setDisplayName(scheme.displayName);
      setDailyAmount(scheme.dailyAmount.toString());
      setDurationDays(scheme.durationDays.toString());
      setAgentId(scheme.agentId || '');
      setStatus(scheme.status);
      setStartDate(scheme.startDate);
    }
  }, [scheme]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const dailyAmtPaise = new Decimal(dailyAmount).times(100).round().toNumber();
      const durDays = new Decimal(durationDays).toNumber();
      await updateMutation.mutateAsync({
        id,
        body: {
          displayName,
          dailyAmountPaise: dailyAmtPaise,
          tenureDays: durDays,
          assignedAgentId: agentId || null,
          status,
          startDate,
        },
      });
      toast({
        type: 'success',
        title: 'Scheme Updated',
        description: 'The PIDI daily chit scheme has been successfully updated.',
      });
      setIsEditing(false);
    } catch (err) {
      console.error(err);
      const errorMsg = err instanceof Error ? err.message : 'An error occurred while updating the scheme.';
      toast({
        type: 'error',
        title: 'Update Failed',
        description: errorMsg,
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Skeleton className="h-8 w-1/4" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (error || !scheme) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          Error loading PIDI Daily Scheme details. Please try again.
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full gap-6">
      <PageHeader
        title={scheme.displayName}
        subtitle={"Scheme number: " + scheme.schemeNumber}
        backTo="/pidi-schemes"
        action={
          <div className="flex items-center gap-2">
            <StatusBadge
              status={scheme.status === 'ACTIVE' ? 'success' : scheme.status === 'CLOSED' ? 'danger' : 'info'}
              label={scheme.status}
            />
            {!isEditing ? (
              <Button variant="secondary" onClick={() => setIsEditing(true)}>
                <Edit2 className="w-4 h-4 mr-2" />
                Edit Scheme
              </Button>
            ) : (
              <Button variant="secondary" onClick={() => setIsEditing(false)}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Detail/Edit Column */}
        <div className="lg:col-span-2">
          {!isEditing ? (
            <Card className="shadow-sm">
              <CardContent className="p-6 space-y-6">
                <h3 className="text-lg font-semibold text-text-primary">Scheme Configuration</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <span className="block text-xs font-semibold text-text-muted uppercase tracking-wider mb-1">Display Name</span>
                    <span className="text-sm font-medium text-text-primary">{scheme.displayName}</span>
                  </div>
                  <div>
                    <span className="block text-xs font-semibold text-text-muted uppercase tracking-wider mb-1">Scheme Number</span>
                    <span className="text-sm font-medium font-mono text-text-primary">{scheme.schemeNumber}</span>
                  </div>
                  <div>
                    <span className="block text-xs font-semibold text-text-muted uppercase tracking-wider mb-1">Daily Rate</span>
                    <span className="text-sm font-bold text-text-primary">
                      <Money paise={scheme.dailyAmount} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} /> / day
                    </span>
                  </div>
                  <div>
                    <span className="block text-xs font-semibold text-text-muted uppercase tracking-wider mb-1">Duration</span>
                    <span className="text-sm font-medium text-text-primary">{scheme.durationDays} Days</span>
                  </div>
                  <div>
                    <span className="block text-xs font-semibold text-text-muted uppercase tracking-wider mb-1">Assigned Agent</span>
                    <span className="text-sm font-medium text-text-primary">{scheme.agentName}</span>
                  </div>
                  <div>
                    <span className="block text-xs font-semibold text-text-muted uppercase tracking-wider mb-1">Timeline</span>
                    <span className="text-sm font-medium text-text-primary font-mono">{scheme.startDate} to {scheme.endDate}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <form onSubmit={handleSave} className="bg-surface-1 border border-border rounded-[12px] p-6 shadow-sm space-y-6">
              <h3 className="text-lg font-semibold text-text-primary">Edit Scheme Settings</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label="Display Name" required>
                  <Input
                    type="text"
                    required
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                  />
                </FormField>

                <FormField label="Daily Rate (INR)" required>
                  <Input
                    type="number"
                    required
                    min="1"
                    value={dailyAmount}
                    onChange={(e) => setDailyAmount(e.target.value)}
                  />
                </FormField>

                <FormField label="Duration (Days)" required>
                  <Input
                    type="number"
                    required
                    min="1"
                    value={durationDays}
                    onChange={(e) => setDurationDays(e.target.value)}
                  />
                </FormField>

                <FormSelect
                  label="Assigned Agent"
                  required
                  value={agentId}
                  onChange={(e) => setAgentId(e.target.value)}
                  options={agents.map((a: { id: string; fullName: string }) => ({ value: a.id, label: a.fullName }))}
                />

                <FormField label="Start Date" required>
                  <Input
                    type="date"
                    required
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </FormField>

                <FormSelect
                  label="Scheme Status"
                  required
                  value={status}
                  onChange={(e) => setStatus(e.target.value as 'DRAFT' | 'ACTIVE' | 'CLOSED')}
                  // Allowed transitions come from the API (canonical PIDI_SCHEME_TRANSITIONS),
                  // never a client-side mirror.
                  options={[scheme.status, ...(scheme.allowedStatusTransitions ?? [])].map(s => ({ label: s, value: s }))}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-border">
                <Button variant="secondary" type="button" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit" loading={updateMutation.isPending}>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            </form>
          )}
        </div>

        {/* Financial Metrics Panel */}
        <div className="space-y-6">
          <Card className="shadow-sm">
            <CardContent className="p-6 space-y-4">
              <h3 className="text-sm font-semibold text-text-muted uppercase tracking-wider">Collection Progress</h3>
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <span className="text-xs text-text-secondary font-medium">Total Micro-Deposits</span>
                  <h2 className="text-2xl font-bold text-success-text">
                    <Money paise={scheme.collectedAmount} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
                  </h2>
                </div>
                <div className="p-3 bg-success-bg text-success-500 rounded-lg">
                  <TrendingUp className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="p-6 space-y-4">
              <h3 className="text-sm font-semibold text-text-muted uppercase tracking-wider">Quick Info</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm text-text-secondary">
                  <User className="w-4 h-4 text-text-muted" />
                  <span>Agent: <strong className="text-text-primary">{scheme.agentName}</strong></span>
                </div>
                <div className="flex items-center gap-3 text-sm text-text-secondary">
                  <Calendar className="w-4 h-4 text-text-muted" />
                  <span>Duration: <strong className="text-text-primary">{scheme.durationDays} Days</strong></span>
                </div>
                <div className="flex items-center gap-3 text-sm text-text-secondary">
                  <Info className="w-4 h-4 text-text-muted" />
                  <span>Rate: <strong className="text-text-primary"><Money paise={scheme.dailyAmount} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} /> / day</strong></span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
